import React, { useState, useEffect, useMemo } from "react";
import withClinic from "../hooks/with_clinic";
import { ApolloResult, Booking, CheckIn, CheckInWithOrders, Clinic, OrderItemForCheckIn } from "../store";
import { Row, Col, Typography, Empty } from "antd";
import Search from "antd/lib/input/Search";
import DateRange from "../components/date_range";
import { Form } from "antd";
import { Table, Button } from "antd";
import { addDays, format, subDays, startOfWeek, endOfWeek } from "date-fns";
import { useQuery } from "@apollo/client";
import LoadingSpinner from "../components/loading_indicator";
import Error from "../components/apollo_error";
import MultiSelect from "../components/multi_select";
import { FileExcelFilled, PlusCircleFilled } from "@ant-design/icons";
import {  get_checkins_count, get_checkin_out_data, getCheckInOutDataVar } from "../graphql/checkin";
import { Link, useNavigate } from "react-router-dom";
import paths from "../routes/paths";
import { ExportExcel } from "../helpers/excel";
import { formatMoney, getCurrency, formatPhoneNumber } from "../helpers/utils";
import client from "../graphql/apollo_client";
import { export_checkinout, getexportCheckInsVar } from "../graphql/export";
import { get_order_items_for_checkins, getOrderItemsForCheckinsVar } from "../graphql/order";
import HorizontalScrollContainer from "../components/horizontal_scroll_container";

interface P {
  currentClinic?: Clinic;
}

function matchCheckInsToOrderItems(
  checkIns: CheckInWithOrders[],
  orderItems: OrderItemForCheckIn[]
): Map<string, string | number> {
  const priceMap = new Map<string, string | number>();

  const remaining = [...orderItems].sort((a, b) =>
    a.id.localeCompare(b.id)
  );

  const sortedCheckIns = [...checkIns].sort((a, b) =>
    a.id.localeCompare(b.id)
  );

  for (const checkIn of sortedCheckIns) {
    if (checkIn.status !== "CHECKOUT" || !checkIn.order_id) continue;

    const index = remaining.findIndex((item) => {
      if (checkIn.isUsePurchaseService) {
        return (
          item.order_id === checkIn.order_id &&
          item.service_id === checkIn.service.id &&
          (parseFloat(String(item.total)) === 0 ||
            item.total === 0 ||
            item.total === "0")
        );
      }

      return (
        item.order_id === checkIn.order_id &&
        item.service_id === checkIn.service.id
      );
    });

    if (index === -1) continue;

    const [matched] = remaining.splice(index, 1);
    priceMap.set(checkIn.id, matched.total);
  }

  return priceMap;
}

const CheckInPage = (props: P) => {
  const clinicId = props.currentClinic?.id || "";
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState<string | null>(null);
  const [filter, setFilter] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [orderItems, setOrderItems] = useState<OrderItemForCheckIn[]>([]);
  const [ranges, setRanges] = useState({
    startDate: startOfWeek(new Date(), { weekStartsOn: 1 }), // Week starts on Monday
    endDate: endOfWeek(new Date(), { weekStartsOn: 1 }), // Week ends on Sunday
  });
  const [form] = Form.useForm();
  const variables = getCheckInOutDataVar({
    clinicId,
    startDate: ranges.startDate,
    endDate: ranges.endDate,
    searchText,
    take: pageSize,
    skip: (currentPage - 1) * pageSize,
    status: filter.length > 0 ? filter : undefined,
  });
  const { loading, data, error } = useQuery<
    ApolloResult<"checkIns", CheckInWithOrders[]>
  >(get_checkin_out_data, { variables });

  // Get total count for pagination
  const { data: countData } = useQuery<{
    aggregateCheckIn: { _count: { _all: number } };
  }>(get_checkins_count, {
    variables: { where: variables.where },
  });

  useEffect(() => {
    const fetchOrderItems = async () => {
      if (data?.checkIns) {
        const orderIds = [...new Set(
          data.checkIns
            .filter(c => c.status === "CHECKOUT" && c.order_id)
            .map(c => c.order_id as string)
        )];
        if (orderIds.length > 0) {
          const orderItemsResult = await client.query({
            query: get_order_items_for_checkins,
            variables: getOrderItemsForCheckinsVar({ orderIds }),
          });
          setOrderItems(orderItemsResult.data?.orderItems || []);
        } else {
          setOrderItems([]);
        }
      }
    };
    fetchOrderItems();
  }, [data]);

  const priceMap = useMemo(
    () => matchCheckInsToOrderItems(data?.checkIns ?? [], orderItems),
    [data?.checkIns, orderItems]
  );

  const getPrice = (checkIn: CheckInWithOrders): string | number => {
    return priceMap.get(checkIn.id) ?? "";
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <Error error={error} />;
  if (!data) return <Empty />;

  const getTherapistName = (checkIn: CheckInWithOrders): string => {
    if (checkIn.practitioners && checkIn.practitioners.length > 0) {
      const names = checkIn.practitioners
        .filter((p) => p.practitioner_id && p.practitioner)
        .map((p) => p.practitioner?.name)
        .filter((name): name is string => !!name)
        .join(", ");
      if (names) return names;
    }
    return checkIn.practitioner?.name || "-";
  };

  const getHelperName = (checkIn: CheckInWithOrders): string => {
    if (checkIn.practitioners && checkIn.practitioners.length > 0) {
      const names = checkIn.practitioners
        .filter((p) => p.helper_id && p.helper)
        .map((p) => p.helper?.name)
        .filter((name): name is string => !!name)
        .join(", ");
      if (names) return names;
    }
    return checkIn?.helper?.name ?? '-';
  };

  const getMerchantNote = (checkIn: CheckInWithOrders): string => {
    if (!checkIn.orders?.metadata) return "";
    try {
      return JSON.parse(checkIn.orders.metadata)?.merchant_note || "";
    } catch {
      return "";
    }
  };

  const onDateChange = (startDate: Date, endDate: Date) => {
    setRanges({ startDate, endDate });
    setCurrentPage(1); // Reset to first page when date changes
  };
  
  const handlePaginationChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1); // Reset to first page when page size changes
    }
  };
  const columns: any[] = [
    {
      title: "Check-In Time",
      dataIndex: "in_time",
      key: "in_time",
      width: 180,
      editable: false,
      sorter: (a: CheckInWithOrders, b: CheckInWithOrders) =>
        new Date(a.in_time).getTime() - new Date(b.in_time).getTime(),
      render: (v: number, r: CheckInWithOrders) => {
        return (
          <Link to={paths.getCheckInDetailRoute(r.id)}>
            {format(new Date(r.in_time), "dd MMM, yyyy h:mm a")}
          </Link>
        );
      },
    },
    {
      title: "Check-Out Time",
      dataIndex: "out_time",
      key: "out_time",
      width: 180,
      editable: false,
      sorter: (a: CheckInWithOrders, b: CheckInWithOrders) => {
        const timeA = a.out_time ? new Date(a.out_time).getTime() : 0;
        const timeB = b.out_time ? new Date(b.out_time).getTime() : 0;
        return timeA - timeB;
      },
      render: (v: number, r: CheckInWithOrders) => {
        return r.out_time
          ? format(new Date(r.out_time), "dd MMM, yyyy h:mm a")
          : "-";
      },
    },
    {
      title: "Order ID",
      dataIndex: "order_id",
      key: "order_id",
      editable: false,
      width: 170,
      sorter: (a: any, b: any) => {
        const orderIdA = a.orders?.order_id || "";
        const orderIdB = b.orders?.order_id || "";
        return String(orderIdA).localeCompare(String(orderIdB));
      },
      render: (v: any, r: any) => {
        return r.orders?.order_id || "-";
      },
    },
    {
      title: "Service",
      dataIndex: "service",
      key: "service",
      editable: false,
      width: 250,
      sorter: (a: CheckInWithOrders, b: CheckInWithOrders) =>
        a.service.name.toLowerCase().localeCompare(b.service.name.toLowerCase()),
      render: (v: number, r: CheckInWithOrders) => {
        return r.service.name;
      },
    },
    {
      title: "Therapist",
      dataIndex: "practitioner",
      key: "practitioner",
      editable: false,
      width: 120,
      sorter: (a: CheckInWithOrders, b: CheckInWithOrders) =>
        getTherapistName(a)
          .toLowerCase()
          .localeCompare(getTherapistName(b).toLowerCase()),
      render: (v: number, r: CheckInWithOrders) => {
        return getTherapistName(r);
      },
    },
    {
      title: "Helper",
      dataIndex: "helper",
      key: "helper",
      editable: false,
      width: 120,
      sorter: (a: CheckInWithOrders, b: CheckInWithOrders) =>
        getHelperName(a).toLowerCase().localeCompare(getHelperName(b).toLowerCase()),
      render: (v: number, r: CheckInWithOrders) => {
        return getHelperName(r);
      },
    },
    {
      title: "Customer",
      dataIndex: "member",
      key: "member",
      editable: false,
      width: 150,
      sorter: (a: CheckInWithOrders, b: CheckInWithOrders) => {
        const nameA = a.member.clinic_members.length > 0
          ? a.member.clinic_members[0].name
          : a.member.name;
        const nameB = b.member.clinic_members.length > 0
          ? b.member.clinic_members[0].name
          : b.member.name;
        return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
      },
      render: (v: number, r: CheckInWithOrders) => {
        return r.member.clinic_members.length > 0
          ? r.member.clinic_members[0].name
          : r.member.name;
      },
    },
    {
      title: "Seller Name",
      dataIndex: "seller",
      key: "seller",
      editable: false,
      width: 130,
      sorter: (a: any, b: any) => {
        const sellerA = a.orders?.seller?.display_name || "";
        const sellerB = b.orders?.seller?.display_name || "";
        return sellerA.toLowerCase().localeCompare(sellerB.toLowerCase());
      },
      render: (v: number, r: any) => {
        return r.orders?.seller?.display_name || "-";
      },
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      editable: false,
      width: 130,
      sorter: (a: CheckInWithOrders, b: CheckInWithOrders) => {
        const phoneA = a.member.clinic_members.length > 0
          ? a.member.clinic_members[0].phonenumber || a.member.phonenumber
          : a.member.phonenumber;
        const phoneB = b.member.clinic_members.length > 0
          ? b.member.clinic_members[0].phonenumber || b.member.phonenumber
          : b.member.phonenumber;
        return phoneA.localeCompare(phoneB);
      },
      render: (v: number, r: CheckInWithOrders) => {
        const phoneNumber = r.member.clinic_members.length > 0
          ? r.member.clinic_members[0].phonenumber || r.member.phonenumber
          : r.member.phonenumber;
        return formatPhoneNumber(phoneNumber, props.currentClinic) || "-";
      },
    },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: "payment_method",
      editable: false,
      width: 130,
      sorter: (a: any, b: any) => {
        const methodA = a.orders?.payment_method || "";
        const methodB = b.orders?.payment_method || "";
        return methodA.localeCompare(methodB);
      },
      render: (v: number, r: any) => {
        return r.orders?.payment_method || "-";
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      editable: false,
      width: 100,
      sorter: (a: any, b: any) => {
        const statusA = a.orders?.payment_status || "";
        const statusB = b.orders?.payment_status || "";
        return statusA.localeCompare(statusB);
      },
      render: (v: number, r: any) => {
        return r.orders?.payment_status || "-";
      },
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      editable: false,
      align: "right",
      width: 120,
      sorter: (a: any, b: any) => {
        const priceA = getPrice(a);
        const priceB = getPrice(b);
        return Number(priceA || 0) - Number(priceB || 0);
      },
      render: (v: number, r: CheckInWithOrders) => {
        const price = getPrice(r);
        if (!price) return "-";
        return `${formatMoney(Number(price))} ${getCurrency(props.currentClinic)}`;
      },
    },
    {
      title: "Note",
      dataIndex: "merchant_note",
      key: "merchant_note",
      editable: false,
      width: 170,
      render: (_: unknown, r: CheckInWithOrders) => {
        const note = getMerchantNote(r);
        if (!note) return "-";
        return (
          <Typography.Paragraph ellipsis={{ rows: 2, expandable: true }}>
            {note}
          </Typography.Paragraph>
        );
      },
    },
  ];

  const handleExport = async () => {
    const fileName =
      "checkinout_" +
      format(ranges.startDate, "yyyy-MMM-dd") +
      "_" +
      format(ranges.endDate, "yyyy-MMM-dd");
    let excelData: any[] = [];

    let list: any[] = [];
    let rolling = true;
    const take = 500;
    let skip = 0;
    const header = [
      "Check-In Time",
      "Check-Out Time",
      "Order ID",
      "Service",
      "Therapist",
      "Helper",
      "Customer",
      "Seller Name",
      "Phone",
      "Payment Method",
      "Status",
      `Total (${getCurrency(props.currentClinic)})`,
      "Note",
    ];

    setExporting(true);
    while (rolling) {
      const variables = getCheckInOutDataVar({
        clinicId,
        take,
        skip,
        startDate: ranges.startDate,
        endDate: ranges.endDate,
        status: filter.length > 0 ? filter : undefined,
      });
      const result = await client.query<ApolloResult<"checkIns", CheckInWithOrders[]>>({
        query: get_checkin_out_data,
        variables,
      });
      const checkIns = result.data?.checkIns || [];
      list = list.concat(checkIns);
      skip += checkIns.length;
      rolling = checkIns.length === take;
    }
    
    const orderIds = [...new Set(
      list
        .filter(c => c.status === "CHECKOUT" && (c.order_id || c.orders?.order_id))
        .map(c => c.order_id || c.orders?.order_id)
        .filter(Boolean)
    )];
    let exportOrderItems: OrderItemForCheckIn[] = [];
    if (orderIds.length > 0) {
      const orderItemsResult = await client.query({
        query: get_order_items_for_checkins,
        variables: getOrderItemsForCheckinsVar({ orderIds }),
      });
      exportOrderItems = orderItemsResult.data?.orderItems || [];
    }

    const exportPriceMap = matchCheckInsToOrderItems(list, exportOrderItems);

    for (const i of list) {
      const checkInTime = format(new Date(i.in_time), "dd MMM, yyyy h:mm a");
      const checkOutTime = i.out_time
        ? format(new Date(i.out_time), "dd MMM, yyyy h:mm a")
        : "-";
      const orderId = i.orders?.order_id || i.order_id || "-";
      const service = i.service.name;
      const therapist = getTherapistName(i);
      const helper = getHelperName(i);
      const customer = i.member.clinic_members.length > 0
        ? i.member.clinic_members[0].name
        : i.member.name;
      const sellerName = i.orders?.seller?.display_name || "-";
      const phone = formatPhoneNumber(
        i.member.clinic_members.length > 0
          ? i.member.clinic_members[0].phonenumber || i.member.phonenumber
          : i.member.phonenumber,
        props.currentClinic
      ) || "-";
      const paymentMethod = i.orders?.payment_method || "-";
      const status = i.orders?.payment_status || i.status || "-";
      const note = getMerchantNote(i);

      const price = exportPriceMap.get(i.id) ?? "";
      const total = price ? Number(price) : "";

      excelData.push({
        check_in_time: checkInTime,
        check_out_time: checkOutTime,
        order_id: orderId,
        service,
        therapist,
        helper,
        customer,
        seller_name: sellerName,
        phone,
        payment_method: paymentMethod,
        status,
        total,
        note,
      });
    }

    ExportExcel(excelData, fileName, header);
    setExporting(false);
  };

  const control = (
    <Row style={{ marginTop: -18 }}>
      <Col flex={4}>
        <Row>
          <Col span={8}>
            <Typography level={2}>Check In/Out</Typography>
          </Col>
          <Col span={8} offset={8}>
            <Search
              defaultValue={searchText || undefined}
              placeholder="search ...."
              allowClear
              size="middle"
              onSearch={(val: any) => {
                setSearchText(val);
                setCurrentPage(1); // Reset to first page when searching
              }}
            />
          </Col>
        </Row>
      </Col>
      <Col flex={0}>
        <DateRange
          startDate={ranges.startDate}
          endDate={ranges.endDate}
          onSelect={onDateChange}
        />
      </Col>
    </Row>
  );
  const context = (
    <Row style={{ marginTop: 8 }}>
      <Col span={24}>
        <Form form={form} component={false}>
          <HorizontalScrollContainer>
            <Table
              showHeader={data.checkIns.length > 0}
              //components={{ body: { cell: EditableCell, } }}
              // expandable={{ expandedRowRender, onExpandedRowsChange }}
              dataSource={data.checkIns.map((b) => ({ key: b.id, ...b }))}
              columns={columns}
              rowClassName="editable-row"
              scroll={{ x: 1950 }}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: countData?.aggregateCheckIn?._count?._all || 0,
                onChange: handlePaginationChange,
                onShowSizeChange: handlePaginationChange,
                showSizeChanger: true,
                showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} of ${total} items`,
                pageSizeOptions: ['10', '20', '50', '100'],
              }}
            />
          </HorizontalScrollContainer>
        </Form>
      </Col>
    </Row>
  );
  const filters = (
    <Row style={{ marginTop: 4 }}>
      <Col span={20}>
        <MultiSelect
          selected={filter}
          placeholder="Select checkIn status"
          options={["CHECKIN", "CHECKOUT"]}
          onChange={(val) => {
            setFilter(val);
            setCurrentPage(1); // Reset to first page when filter changes
          }}
        />
      </Col>
      <Col span={2}>
        {" "}
        <Button
          size="small"
          icon={<PlusCircleFilled />}
          onClick={() => {
            navigate(paths.newCheckout);
          }}
        >
          Add New
        </Button>
      </Col>
      <Button
        size="small"
        loading={exporting}
        icon={<FileExcelFilled />}
        onClick={() => handleExport()}
      >
        Export
      </Button>
    </Row>
  );

  return (
    <React.Fragment>
      {control}
      {filters}
      {context}
    </React.Fragment>
  );
};

export default withClinic(CheckInPage);
